import { createContext, useContext, useState, useEffect, useCallback } from "react";

export interface FavoriteItem {
  id: number;
  nameAr: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  badge: string;
  rating: number;
  reviews: number;
}

interface FavoritesContextType {
  items: FavoriteItem[];
  totalItems: number;
  isFavorite: (id: number) => boolean;
  toggleFavorite: (item: FavoriteItem) => void;
  removeFavorite: (id: number) => void;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | null>(null);

const STORAGE_KEY = "al_ebdaa_favorites";

function loadFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FavoriteItem[]) : [];
  } catch {
    return [];
  }
}

function saveFavorites(items: FavoriteItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<FavoriteItem[]>(loadFavorites);

  useEffect(() => {
    saveFavorites(items);
  }, [items]);

  const isFavorite = useCallback((id: number) => items.some((i) => i.id === id), [items]);

  const toggleFavorite = useCallback((item: FavoriteItem) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) return prev.filter((i) => i.id !== item.id);
      return [...prev, item];
    });
  }, []);

  const removeFavorite = useCallback((id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearFavorites = useCallback(() => setItems([]), []);

  return (
    <FavoritesContext.Provider
      value={{
        items,
        totalItems: items.length,
        isFavorite,
        toggleFavorite,
        removeFavorite,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
